/**
 * Shopping-list → Food Safety setup / logging suggestions
 * (“Products you might not want to forget” / “From your list”).
 *
 * Hygiene: exclude obvious cleaning / disposables / soft drinks / test
 * lines and placeholder junk titles (`newww`); demote beverage zones;
 * strip MOQ / pack / kilo / colon-SKU noise from chip titles; rank
 * food-ish + prior FS names + recent orders first; Cooking + Cooling
 * prefer protein / hot-kitchen tokens and demote bare produce so chips
 * don’t lead with a fruit bowl; cap length.
 * Default chips: random sample from the ranked pool (stable per seed).
 * Search: relevance filter over the same hygiene-ranked candidates.
 */

import { FOOD_SAFETY_SECTION } from 'src/constants/foodSafety'

export const FOOD_SAFETY_SHOPPING_LIST_SUGGEST_SECTIONS = Object.freeze([
  FOOD_SAFETY_SECTION.COOKING_REHEATING,
  FOOD_SAFETY_SECTION.COOLING,
  FOOD_SAFETY_SECTION.DELIVERIES,
])

/** Setup tab / drawer chip cap (default / unfiltered). */
export const FOOD_SAFETY_SHOPPING_LIST_SETUP_LIMIT = 8

/** Day composer “From your list” chip cap (default / unfiltered). */
export const FOOD_SAFETY_SHOPPING_LIST_LOGGING_LIMIT = 6

/** Calm empty when Cooking/Cooling default chips have nothing kitchen-useful (29.68). */
export const FOOD_SAFETY_SUGGEST_EMPTY_SEARCH_CUE = 'Search your list'

/** Search results cap — slightly higher so typing can surface more matches. */
export const FOOD_SAFETY_SHOPPING_LIST_SEARCH_LIMIT = 16

/**
 * How far down the ranked pool we sample default chips from.
 * Keeps hygiene / food ranking meaningful while avoiding always-first-N.
 */
export const FOOD_SAFETY_SHOPPING_LIST_SAMPLE_POOL_MIN = 24

/**
 * Type / zone tokens that are rarely CCP food lines.
 * Matched as whole words against type + zone + secondaryZone.
 */
const NON_FOOD_TYPE_ZONE_RE = new RegExp(
  [
    'cleaning',
    'clean(?:ing)?\\s*chem(?:ical)?s?',
    'disposables?',
    'consumables?',
    'housekeeping',
    'packaging',
    'chemicals?',
    'non[\\s-]?food',
    'stationery',
    'amenities?',
    'janitorial',
    'laundry',
    'cutlery',
    'disposables?\\s*packaging',
  ].join('|'),
  'i',
)

/**
 * Title patterns for cleaning / paper / disposables / test SKUs.
 * Cheap and intentional — prefer false negatives over hiding real food.
 */
const TITLE_EXCLUDE_RES = [
  /\bcleaning\b/i,
  /\bcartridge\b/i,
  /\bdetergent\b/i,
  /\bdisinfect/i,
  /\bsanitis/i,
  /\bsanitiz/i,
  /\bbleach\b/i,
  /\bdescal/i,
  /\boven\s*clean/i,
  /\bblue\s*rolls?\b/i,
  /\bcentre\s*feed\b/i,
  /\bcenter\s*feed\b/i,
  /\bpaper\s*towel/i,
  /\bhand\s*towel/i,
  /\bwipes?\b/i,
  /\bmops?\b/i,
  /\bcutlery\b/i,
  /\btulip\s*cones?\b/i,
  /\bnapkins?\b/i,
  /\bgloves?\b/i,
  /\bstraws?\b/i,
  /\balumin(?:i)?um\s*foil\b/i,
  /\bcling\s*film\b/i,
  /\btest\s*(product|line|item|sku)\b/i,
  /\bdo\s*not\s*use\b/i,
  // Soft drinks / fizzy — not Cooking / Cooling / Deliveries CCP leads.
  /\bfanta\b/i,
  /\bpepsi\b/i,
  /\bcoca[\s-]?cola\b/i,
  /\bcoke\b/i,
  /\b7[\s-]?up\b/i,
  /\bsprite\b/i,
  /\bdr[\s.]?pepper\b/i,
  /\birn[\s-]?bru\b/i,
  /\bschweppes\b/i,
  /\blucozade\b/i,
  /\bgatorade\b/i,
  /\bpowerade\b/i,
  /\bred[\s-]?bull\b/i,
  /\bribena\b/i,
  /\boasis\b/i,
  /\bappletiser\b/i,
  /\bsan\s*pellegrino\b/i,
  /\bsoft[\s-]?drinks?\b/i,
  /\bfizzy\s*drinks?\b/i,
  /\bcarbonated\b/i,
  /\blemonade\b/i,
  /\bcola\b/i,
]

/**
 * Placeholder / junk SKU titles — never chip these (Q2 / 29.24).
 * Matched against the raw catalogue title and the formatted chip title.
 */
const TITLE_JUNK_RES = [
  /^new+$/i, // new, neww, newww
  /^test+$/i,
  /^asdf+$/i,
  /^xxx+$/i,
  /^n\/?a$/i,
  /^untitled$/i,
  /^product\s*\d*$/i,
  /^item\s*\d*$/i,
  /^sku\s*\d*$/i,
  /^-+$/,
  /^\.+$/,
]

/** Soft demote — still food-ish but rarely a Cooking/Cooling/Deliveries CCP lead. */
const TITLE_DEMOTE_RES = [
  /\bcrackers?\b/i,
  /\bbiscuits?\b/i,
  /\bcrisps?\b/i,
  /\bice\s*cream\b/i,
  /\bsorbet\b/i,
]

/**
 * Bakery / bread aisle — rarely the Cooking/Cooling CCP lead (29.60).
 * e.g. "Bread Thick Granary".
 */
const COOKING_BAKERY_TITLE_RE = new RegExp(
  [
    '\\bbreads?\\b',
    '\\bgranary\\b',
    '\\bbaguettes?\\b',
    '\\bciabatta\\b',
    '\\bsourdough\\b',
    '\\bbloomer\\b',
    '\\brolls?\\b',
    '\\bbuns?\\b',
    '\\bcroissants?\\b',
    '\\bpastr(?:y|ies)\\b',
    '\\btortillas?\\b',
    '\\bwraps?\\b',
  ].join('|'),
  'i',
)

/**
 * Catalogue shout titles (ALL CAPS supplier lines) — still food, poor chip leads
 * (29.60 — e.g. "ALSTON RUMP").
 */
export function isFoodSafetySuggestCatalogueShoutTitle(title) {
  const t = String(title || '').trim()
  if (t.length < 6) return false
  // Must look like letters/digits shout, not a normal sentence-case name.
  if (!/^[A-Z0-9]/.test(t)) return false
  const letters = t.replace(/[^A-Za-z]/g, '')
  if (letters.length < 4) return false
  const upper = letters.replace(/[^A-Z]/g, '').length
  return upper / letters.length >= 0.85
}

/** Residual pack / SKU tokens that survived title cleanup. */
const PACK_SKU_RESIDUAL_RE =
  /\b(?:pack(?:s|ing)?|case|sku|moq|min(?:imum)?\s*order|\d+\s*x\s*\d+)\b/i

/**
 * Type / zone tokens for drinks — demote hard so they never lead chips.
 * (Milk etc. usually sit under Dairy and keep the food boost.)
 */
const BEVERAGE_TYPE_ZONE_RE = new RegExp(
  [
    'beverage',
    'beverages',
    'drink',
    'drinks',
    'soft[\\s-]?drinks?',
    'fizzy',
    'soda',
    'minerals?',
  ].join('|'),
  'i',
)

const FOOD_TYPE_ZONE_RE = new RegExp(
  [
    'food',
    'meat',
    'poultry',
    'fish',
    'seafood',
    'dairy',
    'produce',
    'fruit',
    'veg(?:etable)?s?',
    'bakery',
    'bread',
    'frozen',
    'fridge',
    'freezer',
    'chilled',
    'grocery',
    'protein',
    'dry\\s*goods?',
    'deli',
    'butchery',
  ].join('|'),
  'i',
)

/** Cooking CCP leads — proteins / prep, not raw fruit aisle. */
const COOKING_PROTEIN_TYPE_ZONE_RE = new RegExp(
  [
    'meat',
    'poultry',
    'fish',
    'seafood',
    'protein',
    'butchery',
    'deli',
    'dairy',
  ].join('|'),
  'i',
)

const COOKING_PRODUCE_TYPE_ZONE_RE = new RegExp(
  ['produce', 'fruit', 'veg(?:etable)?s?'].join('|'),
  'i',
)

/**
 * Protein / hot-kitchen titles — boost even when type/zone is missing
 * (many shopping lists only store a title).
 */
const COOKING_PROTEIN_TITLE_RE = new RegExp(
  [
    '\\bchicken\\b',
    '\\bturkey\\b',
    '\\bduck\\b',
    '\\bbeef\\b',
    '\\bpork\\b',
    '\\blamb\\b',
    '\\bveal\\b',
    '\\bbacon\\b',
    '\\bham\\b',
    '\\bsausages?\\b',
    '\\bmince\\b',
    '\\bsteak\\b',
    '\\bfillets?\\b',
    '\\bsalmon\\b',
    '\\bcod\\b',
    '\\bhaddock\\b',
    '\\bcoley\\b',
    '\\bprawns?\\b',
    '\\bshrimp\\b',
    '\\bscampi\\b',
    '\\btuna\\b',
    '\\bmackerel\\b',
    '\\bseafood\\b',
    '\\bmeatballs?\\b',
    '\\bburger\\b',
    '\\bchorizo\\b',
  ].join('|'),
  'i',
)

/** Prepared / reheated dish cues — cook CCP, not raw aisle. */
const COOKING_HOT_KITCHEN_TITLE_RE = new RegExp(
  [
    '\\bcurry\\b',
    '\\bstew\\b',
    '\\bsoup\\b',
    '\\bchilli\\b',
    '\\bchili\\b',
    '\\bbolognese\\b',
    '\\blasa?gne\\b',
    '\\bpie\\b',
    '\\bgravy\\b',
    '\\bsauce\\b',
    '\\broast(?:ed)?\\b',
    '\\bgrill(?:ed)?\\b',
    '\\bcasserole\\b',
    '\\bribs?\\b',
    '\\bkebab\\b',
    '\\bstir[\\s-]?fry\\b',
    '\\breheat',
    '\\bcooked\\b',
  ].join('|'),
  'i',
)

/** Raw produce titles that should not lead Cooking/Cooling chips. */
const COOKING_PRODUCE_TITLE_RE = new RegExp(
  [
    '\\bapples?\\b',
    '\\bbananas?\\b',
    '\\boranges?\\b',
    '\\bpears?\\b',
    '\\bgrapes?\\b',
    '\\blemons?\\b',
    '\\blimes?\\b',
    '\\bstrawberr',
    '\\bblueberr',
    '\\braspberr',
    '\\bmango(?:es)?\\b',
    '\\bpineapple\\b',
    '\\bmelon\\b',
    '\\btomatoes?\\b',
    '\\blettuce\\b',
    '\\bcucumbers?\\b',
    '\\bpotatoes?\\b',
    '\\bonions?\\b',
    '\\bcarrots?\\b',
    '\\bbeans?\\s*runner\\b',
    '\\brunner\\s*beans?\\b',
    '\\bcabbage\\b',
    '\\bbroccoli\\b',
    '\\bcauliflower\\b',
    '\\bpepper\\b',
    '\\bpeppers\\b',
    '\\bspina(?:ch)?\\b',
    '\\bcelery\\b',
    '\\bcourgette\\b',
    '\\bzucchini\\b',
  ].join('|'),
  'i',
)

/** Sections where kitchen CCP > raw produce aisle. */
function prefersKitchenLeadChips(section) {
  return (
    section === FOOD_SAFETY_SECTION.COOKING_REHEATING ||
    section === FOOD_SAFETY_SECTION.COOLING
  )
}

export function isFoodSafetyShoppingListSuggestSection(section) {
  return FOOD_SAFETY_SHOPPING_LIST_SUGGEST_SECTIONS.includes(section)
}

/**
 * Chip-facing title: drop MOQ / pack / SKU noise so chips read as product names.
 */
/**
 * True for placeholder / nonsense catalogue titles (e.g. `newww`).
 */
export function isFoodSafetySuggestJunkTitle(rawTitle) {
  const raw = String(rawTitle || '').trim()
  if (!raw) return true
  if (matchesAny(TITLE_JUNK_RES, raw)) return true
  const formatted = formatFoodSafetySuggestChipTitle(raw)
  if (!formatted) return true
  return matchesAny(TITLE_JUNK_RES, formatted)
}

export function formatFoodSafetySuggestChipTitle(rawTitle) {
  let t = String(rawTitle || '').trim()
  if (!t) return ''

  // Leading catalogue / ERP numeric codes: "10012974 Chicken thighs"
  t = t.replace(/^\d{4,}\s+/, '')

  // Parenthetical MOQ: (Min Order 3kg), (Minimum Order 6), (MOQ 12)
  t = t.replace(/\s*\(\s*(?:min(?:imum)?\s*order|moq)\b[^)]*\)/gi, '')
  // Trailing dash MOQ: "Hake Fillet - Min Order 3kg"
  t = t.replace(/\s*[-–—]\s*(?:min(?:imum)?\s*order|moq)\b.*$/gi, '')
  // Cheap pack/case noise: (Case of 12), (Pack of 24), (Box of 6)
  t = t.replace(/\s*\(\s*(?:case|pack|box|tray)\s+of\s+\d+\s*[^)]*\)/gi, '')
  // Pack-size parens: (Box), (Each), (Kilo), (4 per KG), (6 per KG) kilo
  t = t.replace(
    /\s*\(\s*(?:box|each|kilo|kg|pcs?|units?|\d+\s*per\s*(?:kg|kilo|pack))\s*[^)]*\)/gi,
    '',
  )
  // Trailing unit / weight tails: "… kilo", "… each", "… - 250 GRAMS - each"
  t = t.replace(
    /\s*[-–—]?\s*\d+(?:[.,]\d+)?\s*(?:grams?|grammes?|g|kg|kilos?|ml|ltrs?|litres?|ltr)\b.*$/gi,
    '',
  )
  t = t.replace(/\s*[-–—]\s*(?:each|kilo|kg|box)\s*$/gi, '')
  t = t.replace(/\s+\b(?:each|kilo|kg|box)\s*$/gi, '')

  // Colon SKU paths: "Beef : Featherblade : Steak : 8oz : UK" (spaces optional)
  if (/:/.test(t)) {
    const parts = t
      .split(/\s*:\s*/)
      .map(p => p.trim())
      .filter(Boolean)
      .filter(
        p =>
          !/^(?:uk|eu|usa?|nz|aus?|oz|lb|kg|g|ml|\d+(?:[.,]\d+)?(?:oz|lb|kg|g|ml)?)$/i.test(
            p,
          ),
      )
    if (parts.length >= 2) {
      // Keep up to two meaningful segments — scannable chip, drop SKU tail.
      t = parts.slice(0, 2).join(' ')
    }
  }

  return t.replace(/\s{2,}/g, ' ').trim()
}

export function venueProductSuggestTitle(venueProduct) {
  const raw = String(
    venueProduct?.title || venueProduct?.product?.title || '',
  ).trim()
  return formatFoodSafetySuggestChipTitle(raw)
}

/**
 * Known supplier(s) for a picked product name. Matches the chip title or the
 * raw catalogue title. `name` is set only when every match agrees on one
 * supplier; `options` lists the distinct suppliers found.
 */
export function resolveFoodSafetyProductSupplier(products, productName) {
  const key = String(productName || '')
    .trim()
    .toLowerCase()
  if (!key) return { name: '', options: [] }
  const found = new Map()
  for (const product of products || []) {
    const raw = String(product?.title || product?.product?.title || '')
      .trim()
      .toLowerCase()
    const chip = venueProductSuggestTitle(product).toLowerCase()
    if (raw !== key && chip !== key) continue
    const supplier = String(
      product?.supplierTitle || product?.supplier?.title || '',
    ).trim()
    if (supplier && !found.has(supplier.toLowerCase())) {
      found.set(supplier.toLowerCase(), supplier)
    }
  }
  const options = [...found.values()]
  return { name: options.length === 1 ? options[0] : '', options }
}

function venueProductTypeZoneBlob(venueProduct) {
  const nested = venueProduct?.product
  return [
    venueProduct?.type,
    venueProduct?.zone,
    venueProduct?.secondaryZone,
    nested?.type,
    nested?.zone,
  ]
    .map(v => String(v || '').trim())
    .filter(Boolean)
    .join(' ')
}

function matchesAny(res, text) {
  if (!text) return false
  return res.some(re => re.test(text))
}

/**
 * True when the shopping-list row is suitable for Cooking / Cooling / Deliveries chips.
 */
export function isFoodSafetySuggestibleProduct(venueProduct) {
  const rawTitle = String(
    venueProduct?.title || venueProduct?.product?.title || '',
  ).trim()
  if (isFoodSafetySuggestJunkTitle(rawTitle)) return false

  const title = venueProductSuggestTitle(venueProduct)
  if (!title) return false
  if (venueProduct?.excludeFromGrossProfit === true) return false

  const typeZone = venueProductTypeZoneBlob(venueProduct)
  if (typeZone && NON_FOOD_TYPE_ZONE_RE.test(typeZone)) return false
  if (matchesAny(TITLE_EXCLUDE_RES, title)) return false
  return true
}

function parseOrderedAt(venueProduct) {
  const raw =
    venueProduct?.lastOrderedDate ||
    venueProduct?.lastOrderedAt ||
    venueProduct?.product?.lastOrderedDate
  if (!raw) return 0
  const ms = Date.parse(raw)
  return Number.isFinite(ms) ? ms : 0
}

/**
 * Higher = better chip. Used after hygiene filter.
 * Cooking + Cooling: prefer proteins / hot-kitchen dishes / prior FS names;
 * demote raw produce so chips don’t lead with Bramley/Gala.
 */
export function scoreFoodSafetyShoppingListSuggestion(
  venueProduct,
  { preferredNames = new Set(), section = null } = {},
) {
  const title = venueProductSuggestTitle(venueProduct)
  const key = title.toLowerCase()
  let score = 0

  if (preferredNames.has(key)) score += 100

  const typeZone = venueProductTypeZoneBlob(venueProduct)
  if (typeZone && FOOD_TYPE_ZONE_RE.test(typeZone)) score += 40

  if (prefersKitchenLeadChips(section)) {
    if (typeZone && COOKING_PROTEIN_TYPE_ZONE_RE.test(typeZone)) score += 50
    if (COOKING_PROTEIN_TITLE_RE.test(title)) score += 45
    if (COOKING_HOT_KITCHEN_TITLE_RE.test(title)) score += 35
    if (typeZone && COOKING_PRODUCE_TYPE_ZONE_RE.test(typeZone)) score -= 70
    if (COOKING_PRODUCE_TITLE_RE.test(title)) score -= 80
    // 29.60 — bakery + ALL-CAPS catalogue shout + residual pack/SKU
    if (COOKING_BAKERY_TITLE_RE.test(title)) score -= 65
    if (isFoodSafetySuggestCatalogueShoutTitle(title)) score -= 55
    if (PACK_SKU_RESIDUAL_RE.test(title)) score -= 45
  }

  if (venueProduct?.isFavourite) score += 20

  const orderedAt = parseOrderedAt(venueProduct)
  if (orderedAt > 0) {
    // Recency within ~180 days boosts; older still beats never-ordered.
    const ageDays = (Date.now() - orderedAt) / (24 * 60 * 60 * 1000)
    if (ageDays <= 180) score += 30
    else score += 10
  }

  const allergens = venueProduct?.allergens || venueProduct?.product?.allergens
  if (Array.isArray(allergens) && allergens.length > 0) score += 8

  if (matchesAny(TITLE_DEMOTE_RES, title)) score -= 50

  // Soft drinks are excluded; remaining beverage-zone lines rank far below food.
  if (typeZone && BEVERAGE_TYPE_ZONE_RE.test(typeZone)) score -= 80

  return score
}

function preferredNameSet(existingDefinitions = [], extraNames = []) {
  const set = new Set()
  for (const d of existingDefinitions || []) {
    const name = String(d?.name || '')
      .trim()
      .toLowerCase()
    if (name) set.add(name)
  }
  for (const n of extraNames || []) {
    const name = String(n || '')
      .trim()
      .toLowerCase()
    if (name) set.add(name)
  }
  return set
}

/**
 * Title ↔ search query relevance. 0 = no match.
 */
export function scoreFoodSafetyShoppingListQueryMatch(title, query) {
  const q = String(query || '')
    .trim()
    .toLowerCase()
  if (!q) return 0
  const t = String(title || '')
    .trim()
    .toLowerCase()
  if (!t) return 0
  if (t === q) return 300
  if (t.startsWith(q)) return 200
  const words = t.split(/[\s,/()-]+/).filter(Boolean)
  if (words.some(w => w.startsWith(q))) return 150
  if (t.includes(q)) return 100
  return 0
}

/**
 * Filter + relevance-rank candidates for a search string.
 * Candidates without a title match are dropped.
 *
 * @param {Array<{ id: string, title: string, score?: number }>} candidates
 * @param {string} query
 */
export function filterFoodSafetySuggestionsByQuery(candidates, query) {
  const q = String(query || '').trim()
  if (!q) return candidates || []

  return (candidates || [])
    .map(c => ({
      ...c,
      matchScore: scoreFoodSafetyShoppingListQueryMatch(c.title, q),
    }))
    .filter(c => c.matchScore > 0)
    .sort((a, b) => {
      if (b.matchScore !== a.matchScore) return b.matchScore - a.matchScore
      if ((b.score || 0) !== (a.score || 0)) {
        return (b.score || 0) - (a.score || 0)
      }
      return String(a.title).localeCompare(String(b.title))
    })
}

function hashSeed(seed) {
  const s = String(seed ?? '')
  let h = 2166136261
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

function mulberry32(a) {
  return function next() {
    let t = (a += 0x6d2b79f5)
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Fisher–Yates shuffle. When `seed` is provided, shuffle is deterministic.
 * When omitted, uses Math.random (for “each open” variety).
 */
export function shuffleFoodSafetySuggestions(items, seed) {
  const arr = [...(items || [])]
  const rng =
    seed === undefined || seed === null || seed === ''
      ? Math.random
      : mulberry32(hashSeed(seed))
  for (let i = arr.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rng() * (i + 1))
    const tmp = arr[i]
    arr[i] = arr[j]
    arr[j] = tmp
  }
  return arr
}

/**
 * Random sample from the top of a ranked pool (hygiene still applies).
 * When pool ≤ limit, returns a shuffled copy of the whole pool.
 *
 * Cooking/Cooling: preferKitchenLead keeps demoted produce out of the
 * shuffle when enough non-negative kitchen lines exist (29.14).
 *
 * @param {Array<{ id: string, title: string, score?: number }>} ranked
 * @param {number} limit
 * @param {{ seed?: string|number, preferKitchenLead?: boolean }} [opts]
 */
export function sampleFoodSafetySuggestions(
  ranked,
  limit,
  { seed, preferKitchenLead = false } = {},
) {
  const n = Math.max(0, Number(limit) || 0)
  const list = ranked || []
  if (n <= 0 || list.length === 0) return []

  const poolSize = Math.min(
    list.length,
    Math.max(n * 3, FOOD_SAFETY_SHOPPING_LIST_SAMPLE_POOL_MIN, n),
  )
  let pool = list.slice(0, poolSize)

  if (preferKitchenLead) {
    // score > 0: real kitchen lead. score 0 pack/shout leftovers stay out (29.68).
    const kitchen = pool.filter(c => (c.score ?? 0) > 0)
    // Empty / all-demoted pool: do not pad chips with shout/bakery/pack noise.
    if (kitchen.length === 0) return []
    pool = kitchen
  }

  return shuffleFoodSafetySuggestions(pool, seed)
    .slice(0, n)
    .map(({ id, title }) => ({ id, title }))
}

function stripCandidate({ id, title }) {
  return { id, title }
}

/**
 * Unique shopping-list candidates for a section, hygiene-filtered and ranked.
 * Does not cap or shuffle — callers sample / search from this pool.
 *
 * @returns {Array<{ id: string, title: string, score: number }>}
 */
export function foodSafetyProductMatchesSupplier(product, supplierName) {
  const want = String(supplierName || '')
    .trim()
    .toLowerCase()
  if (!want) return true
  const have = String(product?.supplierTitle || product?.supplier?.title || '')
    .trim()
    .toLowerCase()
  return have === want
}

export function listFoodSafetyShoppingListCandidates({
  products = [],
  existingDefinitions = [],
  section,
  preferredNames = [],
  supplierName = '',
} = {}) {
  if (!isFoodSafetyShoppingListSuggestSection(section)) return []

  const existingNames = new Set(
    (existingDefinitions || [])
      .filter(
        d =>
          d &&
          d.section === section &&
          d.active !== false &&
          String(d.name || '').trim(),
      )
      .map(d => String(d.name).trim().toLowerCase()),
  )

  // Prefer names already used as FS templates in any section (incl. inactive).
  const preferred = preferredNameSet(existingDefinitions, preferredNames)

  const seen = new Set()
  const candidates = []
  for (const product of products || []) {
    if (!foodSafetyProductMatchesSupplier(product, supplierName)) continue
    if (!isFoodSafetySuggestibleProduct(product)) continue
    const title = venueProductSuggestTitle(product)
    const key = title.toLowerCase()
    if (existingNames.has(key) || seen.has(key)) continue
    seen.add(key)
    candidates.push({
      id: String(product._id || key),
      title,
      score: scoreFoodSafetyShoppingListSuggestion(product, {
        preferredNames: preferred,
        section,
      }),
    })
  }

  candidates.sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score
    return a.title.localeCompare(b.title)
  })

  return candidates
}

/**
 * Unique shopping-list titles not already covered by an active definition
 * in this section. Filtered + ranked for Food Safety use.
 *
 * Default (no query): random sample from the ranked pool (seed optional).
 * With query: relevance-ranked matches (no shuffle).
 * Pass `shuffle: false` to take strict top-N (tests / ranking asserts).
 *
 * @param {{
 *   products?: object[],
 *   existingDefinitions?: Array<{ section?: string, name?: string, active?: boolean }>,
 *   section?: string,
 *   limit?: number,
 *   preferredNames?: string[],
 *   query?: string,
 *   sampleSeed?: string|number,
 *   shuffle?: boolean,
 * }} args
 * @returns {Array<{ id: string, title: string }>}
 */
export function buildFoodSafetyShoppingListSuggestions({
  products = [],
  existingDefinitions = [],
  section,
  limit = FOOD_SAFETY_SHOPPING_LIST_SETUP_LIMIT,
  preferredNames = [],
  query = '',
  sampleSeed,
  shuffle = true,
  supplierName = '',
} = {}) {
  const candidates = listFoodSafetyShoppingListCandidates({
    products,
    existingDefinitions,
    section,
    preferredNames,
    supplierName,
  })

  const q = String(query || '').trim()
  if (q) {
    const matched = filterFoodSafetySuggestionsByQuery(candidates, q)
    const searchLimit = Math.max(
      Math.max(0, limit),
      FOOD_SAFETY_SHOPPING_LIST_SEARCH_LIMIT,
    )
    return matched.slice(0, searchLimit).map(stripCandidate)
  }

  const n = Math.max(0, limit)
  if (!shuffle) {
    return candidates.slice(0, n).map(stripCandidate)
  }

  return sampleFoodSafetySuggestions(candidates, n, {
    seed: sampleSeed,
    preferKitchenLead: prefersKitchenLeadChips(section),
  })
}

/**
 * Shopping-list titles for day logging that are not already quick picks.
 * Order: Quick pick → these chips → free type.
 */
export function buildFoodSafetyLoggingProductSuggestions({
  products = [],
  templates = [],
  section,
  limit = FOOD_SAFETY_SHOPPING_LIST_LOGGING_LIMIT,
  preferredNames = [],
  existingDefinitions = [],
  query = '',
  sampleSeed,
  shuffle = true,
  supplierName = '',
} = {}) {
  if (!isFoodSafetyShoppingListSuggestSection(section)) return []
  const asDefs = (templates || []).map(t => ({
    section,
    name: t?.name,
    active: true,
  }))
  return buildFoodSafetyShoppingListSuggestions({
    products,
    existingDefinitions: [...(existingDefinitions || []), ...asDefs],
    section,
    limit,
    preferredNames,
    query,
    sampleSeed,
    shuffle,
    supplierName,
  })
}

/** Fresh seed for “randomise each open” (drawer / section change). */
export function nextFoodSafetySuggestSampleSeed(prefix = 'fs-list') {
  return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1e6)}`
}
